/**
 * Ranking (PLAN §3.4): `score = match × 1.0 + frecency × 350 + boost`.
 *
 *   match     the fuzzy scorer over title (1.0) / keyLabel (0.95) /
 *             subtitle (0.6) / searchFields (their own weight, keywords 0.4)
 *   frecency  0..1 from frecency.js; people and chats also count recent
 *             conversation activity (`ts`), Slack-style
 *   boost     current page's provider +60, current API workspace / project
 *             +40, open tabs +30
 *
 * A result whose fields do not all match is kept only when it carries a
 * `snippet` or `baseScore` (body / message hits): its match is then
 * `baseScore × 600` with no title highlight (§12.1 rule 6).
 * Tie-break: recency (`ts`), then the shorter title, then id — deterministic.
 */
import { prepare, scoreTerms, scorePrepared } from './fuzzy';
import { frecencyOf, HALF_LIFE_MS } from './frecency';
import { refOf } from './types';

export const FRECENCY_WEIGHT = 350;
export const BOOST_PAGE = 60;
export const BOOST_WORKSPACE = 40;
export const BOOST_OPEN_TAB = 30;
export const TOP_HIT_MIN = 700;
export const TOP_HIT_MARGIN = 1.25;

/** Providers whose `ts` is conversation activity (feeds frecency, §3.4). */
const ACTIVITY_PROVIDERS = new Set(['people', 'chats']);

const FIELDS = Symbol('paletteFields');

/** The scorer's fields for a result; cached on the result object (in-memory providers reuse them). */
export function fieldsFor(result) {
  const hit = result[FIELDS];
  if (hit && hit.title === result.title && hit.subtitle === result.subtitle) return hit.fields;
  const fields = buildFields(result);
  if (Object.isExtensible(result)) {
    Object.defineProperty(result, FIELDS, { value: { fields, title: result.title, subtitle: result.subtitle }, enumerable: false, configurable: true, writable: true });
  }
  return fields;
}

export function buildFields(result) {
  const fields = [];
  if (result.title) fields.push({ prepared: prepare(result.title), weight: 1, name: 'title' });
  if (result.keyLabel) fields.push({ prepared: prepare(result.keyLabel), weight: 0.95, name: 'keyLabel' });
  if (result.subtitle) fields.push({ prepared: prepare(result.subtitle), weight: 0.6, name: 'subtitle' });
  (result.searchFields || []).forEach((f, i) => {
    if (f && f.text) fields.push({ prepared: prepare(f.text), weight: f.weight ?? 0.4, name: `field${i}` });
  });
  return fields;
}

/** The match part only: `{ match, matches }` or null when it must be dropped. */
export function matchResult(result, terms) {
  if (!terms || !terms.length) return { match: 0, matches: [] };
  const r = scoreTerms(terms, fieldsFor(result));
  if (r) {
    // Highlights only for the displayed fields (§12.1 Result.matches).
    const matches = r.matches.filter((m) => m.field === 'title' || m.field === 'subtitle' || m.field === 'keyLabel');
    return { match: r.score, matches };
  }
  if (result.snippet || Number.isFinite(result.baseScore)) {
    return { match: Math.round(Math.max(0, Math.min(1, result.baseScore ?? 0.5)) * 600), matches: [] };
  }
  return null;
}

/**
 * The final score only — no copies, no highlight ranges (candidate selection
 * over thousands of in-memory rows). Same formula as rankResult.
 */
export function quickScore(result, terms, opts = {}) {
  let match = 0;
  if (terms && (opts.prepared ? opts.prepared.ts.length : terms.length)) {
    const fields = opts.fields || fieldsFor(result);
    const s = opts.prepared ? scorePrepared(opts.prepared, fields, true) : scoreTerms(terms, fields, { scoreOnly: true });
    if (s === null) {
      if (!result.snippet && !Number.isFinite(result.baseScore)) return null;
      match = Math.round(Math.max(0, Math.min(1, result.baseScore ?? 0.5)) * 600);
    } else match = s;
  }
  const now = opts.now ?? Date.now();
  let f = 0;
  if (opts.useFrecency !== false) {
    f = (opts.frecency || frecencyOf)(refOf(result.provider, result.id), now);
    if (ACTIVITY_PROVIDERS.has(result.provider)) f = Math.max(f, activityScore(result.ts, now));
  }
  return Math.round(match + FRECENCY_WEIGHT * f);
}

export function activityScore(ts, now = Date.now()) {
  if (!Number.isFinite(ts) || ts <= 0) return 0;
  return 0.6 * 2 ** (-Math.max(0, now - ts) / HALF_LIFE_MS);
}

/**
 * @param {object} result
 * @param {string[]} terms
 * @param {{ ctx?: object, now?: number, pageProvider?: string|null, openRefs?: Set<string>, frecency?: Function, useFrecency?: boolean }} opts
 */
export function rankResult(result, terms, opts = {}) {
  const m = matchResult(result, terms);
  if (!m) return null;
  const now = opts.now ?? Date.now();
  const ref = refOf(result.provider, result.id);
  let f = 0;
  if (opts.useFrecency !== false) {
    f = (opts.frecency || frecencyOf)(ref, now);
    if (ACTIVITY_PROVIDERS.has(result.provider)) f = Math.max(f, activityScore(result.ts, now));
  }
  let boost = 0;
  if (opts.pageProvider && opts.pageProvider === result.provider) boost += BOOST_PAGE;
  const ws = opts.ctx?.currentWorkspaceId;
  const pr = opts.ctx?.activeProjectId;
  const p = result.payload || {};
  if ((ws != null && p.workspaceId != null && String(p.workspaceId) === String(ws)) || (pr && pr !== 'all' && pr !== 'none' && p.projectId != null && String(p.projectId) === String(pr))) {
    boost += BOOST_WORKSPACE;
  }
  if (opts.openRefs && opts.openRefs.has(ref)) boost += BOOST_OPEN_TAB;
  return { ...result, score: Math.round(m.match + FRECENCY_WEIGHT * f + boost), matches: m.matches };
}

export function compareRanked(a, b) {
  return (
    (b.score ?? 0) - (a.score ?? 0) ||
    (b.ts ?? 0) - (a.ts ?? 0) ||
    String(a.title || '').length - String(b.title || '').length ||
    (String(a.id) < String(b.id) ? -1 : String(a.id) > String(b.id) ? 1 : 0)
  );
}

/** Rank, drop non-matches, sort. Stable for equal keys. */
export function rankAll(results, terms, opts) {
  const out = [];
  for (const r of results || []) {
    if (!r) continue;
    const ranked = rankResult(r, terms, opts);
    if (ranked) out.push(ranked);
  }
  return out.sort(compareRanked);
}

/**
 * The single best result across groups, when it beats the runner-up by the
 * margin (≥ 700 and ≥ 1.25×). An exact task-key match always wins (§3.4).
 * @param {object[]} candidates the head of every group
 */
export function pickTopHit(candidates, { exactKey = null } = {}) {
  const list = (candidates || []).filter(Boolean).slice().sort(compareRanked);
  if (!list.length) return null;
  if (exactKey) {
    const key = String(exactKey).toUpperCase();
    const k = list.find((r) => r.keyLabel && String(r.keyLabel).toUpperCase() === key);
    if (k) return k;
  }
  const [best, second] = list;
  if ((best.score ?? 0) < TOP_HIT_MIN) return null;
  if (second && (best.score ?? 0) < TOP_HIT_MARGIN * (second.score ?? 0)) return null;
  return best;
}
